import { ABP, ListService, PagedResultDto, TrackByService } from '@abp/ng.core';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { DateAdapter } from '@abp/ng.theme.shared/extensions';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import type {
  GetAssetComponentsInput,
  AssetComponentWithNavigationPropertiesDto,
} from '../../../proxy/asset-components/models';
import { AssetComponentService } from '../../../proxy/asset-components/asset-component.service';

@Component({
  selector: 'lib-asset-component',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [ListService, { provide: NgbDateAdapter, useClass: DateAdapter }],
  templateUrl: './asset-component.component.html',
  styles: [],
})
export class AssetComponentComponent implements OnInit {
  data: PagedResultDto<AssetComponentWithNavigationPropertiesDto> = {
    items: [],
    totalCount: 0,
  };

  filters = {} as GetAssetComponentsInput;

  form: FormGroup;

  isFiltersHidden = true;

  isModalBusy = false;

  isModalOpen = false;

  selected?: AssetComponentWithNavigationPropertiesDto;

  constructor(
    public readonly list: ListService,
    public readonly track: TrackByService,
    public readonly service: AssetComponentService,
    private confirmation: ConfirmationService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    const getData = (query: ABP.PageQueryParams) =>
      this.service.getList({
        ...query,
        ...this.filters,
        filterText: query.filter,
      });

    const setData = (list: PagedResultDto<AssetComponentWithNavigationPropertiesDto>) => (this.data = list);

    this.list.hookToQuery(getData).subscribe(setData);
  }

  clearFilters() {
    this.filters = {} as GetAssetComponentsInput;
  }

  buildForm() {
    const {
      code,
      assetId,
      componentId,
    } = this.selected?.assetComponent || {};

    this.form = this.fb.group({
      code: [code ?? null, [Validators.required]],
      assetId: [assetId ?? null, [Validators.required]],
      componentId: [componentId ?? null, [Validators.required]],
    });
  }

  hideForm() {
    this.isModalOpen = false;
    this.form.reset();
  }

  showForm() {
    this.buildForm();
    this.isModalOpen = true;
  }

  submitForm() {
    if (this.form.invalid) return;

    const request = this.selected
      ? this.service.update(this.selected.assetComponent.id, this.form.value)
      : this.service.create(this.form.value);

    this.isModalBusy = true;

    request
      .pipe(
        finalize(() => (this.isModalBusy = false)),
        tap(() => this.hideForm()),
      )
      .subscribe(this.list.get);
  }

  create() {
    this.selected = undefined;
    this.showForm();
  }

  update(record: AssetComponentWithNavigationPropertiesDto) {
    this.selected = record;
    this.showForm();
  }

  delete(record: AssetComponentWithNavigationPropertiesDto) {
    this.confirmation.warn(
      'Inventory::DeleteConfirmationMessage',
      'Inventory::AreYouSure',
      { messageLocalizationParams: [] }
    ).pipe(
      filter(status => status === Confirmation.Status.confirm),
      switchMap(() => this.service.delete(record.assetComponent.id)),
    ).subscribe(this.list.get);
  }
}
